/**
 * Sync telemetry — fire-and-forget, NO-PII operational metrics for the new-sync
 * engine (`VITE_USE_NEW_SYNC`).
 *
 * WHY this exists: the new sync engine's full-resync recovery path
 * (`recoverViaFullResync`) and the shared scheduler's rate-limit give-up
 * (`SyncSchedulerTimeout`) are the two failure modes we're diagnosing and need
 * to SIZE before re-enabling the flag.
 *
 * TRANSPORT: telemetry is POSTed to the authenticated in-house endpoint
 * `POST /umbra/telemetry` using a BARE `axios.post` that injects ONLY the bearer
 * token (see {@link sendTelemetry} for why we do NOT use the shared `requestApi`
 * client). It deliberately does NOT use the crash-report transport
 * (`error-boundary/queue` → the PUBLIC + UNAUTHENTICATED Rust `feedback`
 * microservice) — operational data should not ride an unauthenticated public
 * channel. The telemetry endpoint is SEPARATE from the sync endpoints, so
 * telemetry does NOT consume the sync rate budget it is measuring.
 *
 * CRITICAL design constraint (Codex review): the failure mode we are measuring
 * is a *loop bug*. Naive instrumentation in a hot loop would FLOOD the very
 * error pipeline we share. Every guard in {@link ./syncTelemetryGuards} exists
 * to make a flood structurally impossible:
 *   - per-event-type rate limit (≤1 identical eventName / 10 min, persisted),
 *   - per-`syncRunId` dedupe (≤1 identical eventName per run — the loop defense),
 *   - per-client daily cap (soft ~20 + a HARD ceiling that also bounds anomaly
 *     events so a fresh-syncRunId-per-run loop still can't flood),
 *   - payload size cap + schema validation (incl. a PII key denylist) before
 *     enqueue.
 *
 * NO PII: counts / reasons / durations / buckets only. NEVER chat ids, titles,
 * participant ids, usernames, server cursors, or raw error strings.
 */

import axios from "axios";
import APP_VERSION from "@/config/version";
import { createLogger } from "@/utils/debug";
import { ApiEndpoints } from "@/server/apiEndpoints";
import { useAuthState } from "@/zustand/AuthState";
import {
  SCHEMA_VERSION,
  type SyncTelemetryEventName,
  type SyncTelemetryEnvelope,
  passesGuards,
} from "./syncTelemetryGuards";
import {
  getOrCreateClientId,
  bucketChatCount,
  collectNetworkState,
} from "./syncTelemetryContext";

const log = createLogger("SyncTelemetry");

/** Hard cap on the serialized envelope size — drop oversized payloads. */
const MAX_PAYLOAD_BYTES = 4_096;

/**
 * Data the caller may attach to an event. Every value is a count / reason /
 * duration / bucket — NEVER an id, title, username, cursor, or raw error. The
 * type intentionally only admits primitives + the small known shapes so a
 * caller cannot accidentally pass an object graph carrying PII.
 */
export type SyncTelemetryData = Record<
  string,
  string | number | boolean | null | undefined
>;

/**
 * Build the envelope, run the anti-flood guards, validate, size-check, and
 * enqueue. Fire-and-forget: never throws, never blocks the sync run.
 *
 * @param eventName  one of the fixed lifecycle/anomaly event names
 * @param syncRunId  UUID generated once per `runSync` invocation (dedupe key)
 * @param data       counts/reasons/durations/buckets only (NO PII)
 */
export function reportSyncTelemetry(
  eventName: SyncTelemetryEventName,
  syncRunId: string,
  data: SyncTelemetryData = {},
): void {
  try {
    const envelope: SyncTelemetryEnvelope = {
      schemaVersion: SCHEMA_VERSION,
      eventName,
      timestamp: new Date().toISOString(),
      appVersion: APP_VERSION,
      syncRunId,
      clientId: getOrCreateClientId(),
      accountSizeBucket: bucketChatCount(
        typeof data.localChatCount === "number"
          ? data.localChatCount
          : undefined,
      ),
      networkState: collectNetworkState(),
      data: scrubData(data),
    };

    // Anti-flood + schema guards. Returns the (possibly annotated) envelope to
    // send, or null if it should be dropped. Annotations: `suppressedCount`
    // (collapsed dupes in this run) and `droppedToday` (events the daily cap
    // dropped since the last allowed event).
    const allowed = passesGuards(envelope);
    if (!allowed) {
      return;
    }

    const serialized = JSON.stringify(allowed);
    // Payload size cap — drop anything implausibly large rather than ship it.
    if (byteLength(serialized) > MAX_PAYLOAD_BYTES) {
      return;
    }

    sendTelemetry(allowed);
  } catch (err) {
    // Telemetry must be invisible on failure — never re-enter the error path or
    // disturb the sync run. Guard the log call itself so a throwing logger sink
    // cannot make this function throw (the "never throws" contract).
    try {
      log.warn("reportSyncTelemetry failed", err);
    } catch {
      // intentionally empty — telemetry logging must never escape
    }
  }
}

/**
 * Fire-and-forget POST to the authenticated in-house telemetry endpoint.
 *
 * IMPORTANT — we deliberately do NOT route this through the shared
 * `requestApi`/`client` axios instance (`@/server/apiService`). That instance
 * carries response interceptors that make a request a behavioral ACTOR, not a
 * passive observer: a 401 triggers a token-refresh attempt and, if refresh
 * fails, `resetAuthState()` — i.e. a best-effort telemetry call could log the
 * user out. It also runs the X-Min-Client-Version gate and Stripe-403 handling.
 * Telemetry must never cause any of that. So we use a bare `axios` POST that
 * injects ONLY the bearer token (read from the same `useAuthState` source the
 * interceptor reads) and has no interceptors.
 *
 * We do NOT await it and we swallow every error: telemetry must never throw,
 * block the sync run, or surface a failure (the endpoint returns 204 and never
 * returns data). If no token is present we skip silently (the endpoint is
 * authenticated; an unauthenticated POST would just 401).
 */
function sendTelemetry(envelope: SyncTelemetryEnvelope): void {
  const token = useAuthState.getState().tk;
  if (!token) {
    return;
  }
  void axios
    .post(ApiEndpoints.TELEMETRY, envelope, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .catch((err) => {
      // Swallow — best-effort. Guard the log call itself so a throwing logger
      // sink can never turn this into an unhandled rejection. Telemetry stays
      // invisible to the user and never re-enters the sync/error path.
      try {
        log.warn("telemetry POST failed (ignored)", err);
      } catch {
        // intentionally empty — telemetry logging must never escape
      }
    });
}

/**
 * Strip `localChatCount` (it is bucketed into `accountSizeBucket` and must not
 * be emitted exactly) and drop any undefined values so the envelope stays
 * compact. This is a defensive second pass — the typed surface already blocks
 * objects, but we explicitly remove the one numeric field that could be
 * re-identifying at small account sizes.
 */
function scrubData(data: SyncTelemetryData): SyncTelemetryData {
  const out: SyncTelemetryData = {};
  for (const [key, value] of Object.entries(data)) {
    if (key === "localChatCount") {
      continue; // bucketed; never emit the exact count
    }
    if (value === undefined) {
      continue;
    }
    out[key] = value;
  }
  return out;
}

function byteLength(s: string): number {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(s).length;
  }
  // Fallback (older runtimes / jsdom without TextEncoder): UTF-16 length is a
  // safe over-estimate for the ASCII-dominant telemetry payloads.
  return s.length;
}
